// v2/pages/admin/AdminCampaignsV2.js — Yonetim / Kampanyalar (/v2/admin/campaigns)
// URL: ?q= &code= &type= &scope= &status= &period= &group= &sort= &dir= &page= &per=
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Percent, Search, X, SlidersHorizontal, RotateCw, Plus, ChevronLeft, ChevronRight,
  FileSpreadsheet,
  ChevronDown, ChevronUp, ChevronsUpDown, Tag, Package, Boxes, Store, Globe,
  Calendar, Clock, AlertTriangle, Inbox, Loader2, Pencil, Trash2, Power, PowerOff,
  TrendingDown,
} from 'lucide-react';
import useAdminCampaigns, { adminCampaignApi } from '../../hooks/useAdminCampaigns';
import CampaignModal from '../../components/admin/CampaignModal';
import BulkCampaignModal from '../../components/admin/BulkCampaignModal';
import ToastStack from '../../components/Toast';
import useToast from '../../hooks/useToast';
import { formatNumber, formatPercent } from '../../utils/format';
import { SkelPanel } from '../../components/Skeleton';
import s from '../../styles/AdminCampaignsV2.module.css';
import '../../styles/tokens.css';

const TIPLER = [
  { id: '', label: 'Tüm tipler' },
  { id: 'Ürün Grubu', label: 'Ürün Grubu' },
  { id: 'Ürün', label: 'Tek Ürün' },
];
const KAPSAMLAR = [
  { id: '', label: 'Tüm kapsamlar' },
  { id: 'customer', label: 'Müşteriye özel' },
  { id: 'general', label: 'Genel' },
];
const DONEMLER = [
  { id: '', label: 'Tüm dönemler' },
  { id: 'live', label: 'Yürürlükte' },
  { id: 'expired', label: 'Süresi geçmiş' },
  { id: 'future', label: 'Başlamamış' },
];
const DURUMLAR = ['', 'Aktif', 'Pasif', 'Gelecek'];
const VARSAYILAN = { sort: 'id', dir: 'desc', page: '1', per: '25' };

const tarihTR = (iso) => {
  if (!iso) return '-';
  const [y, a, g] = String(iso).slice(0, 10).split('-');
  return y ? `${g}.${a}.${y}` : '-';
};
const suresiz = (iso) => iso && Number(String(iso).slice(0, 4)) >= 2039;

function efektif(oranlar) {
  const g = (oranlar || []).filter((x) => x > 0 && x < 100);
  if (!g.length) return null;
  return Math.round((1 - g.reduce((a, o) => a * (1 - o / 100), 1)) * 10000) / 100;
}

export default function AdminCampaignsV2() {
  const [sp, setSp] = useSearchParams();
  const oku = useCallback((k) => sp.get(k) || VARSAYILAN[k] || '', [sp]);
  const { items: toastlar, push, dismiss } = useToast();

  const [arama, setArama] = useState(sp.get('q') || '');
  const [filtreAcik, setFiltreAcik] = useState(false);
  const [modal, setModal] = useState(null);   // {} = yeni, kayit = duzenle
  const [topluAcik, setTopluAcik] = useState(false);
  const [silinecek, setSilinecek] = useState(null);
  const [islemde, setIslemde] = useState(false);

  useEffect(() => { setArama(sp.get('q') || ''); }, [sp]);

  const params = useMemo(() => ({
    q: sp.get('q') || '', code: sp.get('code') || '', type: sp.get('type') || '',
    scope: sp.get('scope') || '', status: sp.get('status') || '',
    period: sp.get('period') || '', group: sp.get('group') || '',
    sort: oku('sort'), dir: oku('dir'), page: oku('page'), per: oku('per'),
  }), [sp, oku]);

  const { data, loading, error, yenile } = useAdminCampaigns(params);

  const guncelle = useCallback((yeni, sayfaSifirla = true) => {
    setSp((onceki) => {
      const p = new URLSearchParams(onceki);
      Object.entries(yeni).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined || v === VARSAYILAN[k]) p.delete(k);
        else p.set(k, v);
      });
      if (sayfaSifirla) p.delete('page');
      return p;
    }, { replace: true });
  }, [setSp]);

  useEffect(() => {
    const suAn = sp.get('q') || '';
    if (arama === suAn) return undefined;
    const t = setTimeout(() => guncelle({ q: arama.trim() }), 400);
    return () => clearTimeout(t);
  }, [arama, sp, guncelle]);

  const sirala = (alan) => {
    if (!alan) return;
    const ayni = params.sort === alan;
    guncelle({ sort: alan, dir: ayni && params.dir === 'desc' ? 'asc' : 'desc' });
  };

  const durumDegistir = useCallback(async (k) => {
    setIslemde(true);
    try {
      const yeni = k.status === 'Aktif' ? 'Pasif' : 'Aktif';
      const v = await adminCampaignApi.durum(k.id, yeni);
      if (!v || !v.success) throw new Error((v && v.message) || 'İşlem başarısız');
      push(v.message, 'success');
      yenile();
    } catch (e) {
      push((e && e.message) || 'İşlem başarısız', 'error');
    } finally { setIslemde(false); }
  }, [push, yenile]);

  const sil = useCallback(async () => {
    if (!silinecek) return;
    setIslemde(true);
    try {
      const v = await adminCampaignApi.sil(silinecek.id);
      if (!v || !v.success) throw new Error((v && v.message) || 'Silinemedi');
      push('Kampanya silindi', 'success');
      setSilinecek(null);
      yenile();
    } catch (e) {
      push((e && e.message) || 'Silinemedi', 'error');
    } finally { setIslemde(false); }
  }, [silinecek, push, yenile]);

  const ilk = loading && !data;
  const ogeler = (data && data.items) || [];
  const o = (data && data.summary) || {};
  const gruplar = (data && data.groups) || [];
  const sayfa = (data && data.page) || 1;
  const sonSayfa = (data && data.total_pages) || 1;
  const toplam = (data && data.total) || 0;
  const per = Number(params.per) || 25;
  const ilkSira = (sayfa - 1) * per;
  const acikFiltre = ['code', 'type', 'scope', 'status', 'period', 'group']
    .filter((k) => params[k]).length;

  const SiraIkon = ({ alan }) => {
    if (!alan) return null;
    if (params.sort !== alan) return <ChevronsUpDown size={13} className={s.sortIdle} />;
    return params.dir === 'asc'
      ? <ChevronUp size={13} className={s.sortOn} />
      : <ChevronDown size={13} className={s.sortOn} />;
  };

  const Oranlar = ({ rates }) => {
    const ef = efektif(rates);
    return (
      <span className={s.rateCell}>
        <span className={s.rateChain}>
          {(rates || []).map((r, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className={s.rateSep}>+</span>}
              <span className={s.rateItem}>{String(r).replace('.', ',')}</span>
            </React.Fragment>
          ))}
        </span>
        {ef !== null && rates.length > 1 && (
          <span className={s.rateEff}>= %{String(ef).replace('.', ',')}</span>
        )}
      </span>
    );
  };

  return (
    <div className={s.page}>
      <div className={s.head}>
        <div className={s.headText}>
          <h2 className={s.title}>Kampanyalar</h2>
          <p className={s.subtitle}>
            {ilk ? 'Yükleniyor…'
              : <><b>{formatNumber(toplam)}</b> kampanya · indirimler kademeli uygulanır</>}
          </p>
        </div>
        <div className={s.headBtns}>
          <button type="button" className={s.ghostBtn} onClick={yenile} disabled={loading}>
            <RotateCw size={15} className={loading ? s.spin : ''} />
            <span className={s.btnText}>Yenile</span>
          </button>
          <button type="button" className={s.ghostBtn} onClick={() => setTopluAcik(true)}>
            <FileSpreadsheet size={16} /> <span className={s.btnText}>Toplu Yükle</span>
          </button>
          <button type="button" className={s.primaryBtn} onClick={() => setModal({})}>
            <Plus size={16} /> <span className={s.btnText}>Yeni Kampanya</span>
          </button>
        </div>
      </div>

      <div className={s.stats}>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${s.tBlue}`}><Percent size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatNumber(o.campaigns)}</span>
            <span className={s.statLabel}>Toplam kampanya</span>
          </span>
        </div>
        <button type="button"
          className={`${s.stat} ${s.statBtn} ${params.period === 'live' ? s.statOn : ''}`}
          onClick={() => guncelle({ period: params.period === 'live' ? '' : 'live' })}>
          <span className={`${s.statIcon} ${s.tGreen}`}><Clock size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatNumber(o.live)}</span>
            <span className={s.statLabel}>Yürürlükte</span>
          </span>
        </button>
        <button type="button"
          className={`${s.stat} ${s.statBtn} ${params.period === 'expired' ? s.statOn : ''}`}
          onClick={() => guncelle({ period: params.period === 'expired' ? '' : 'expired' })}>
          <span className={`${s.statIcon} ${s.tSlate}`}><Calendar size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatNumber(o.expired)}</span>
            <span className={s.statLabel}>Süresi geçmiş</span>
          </span>
        </button>
        <button type="button"
          className={`${s.stat} ${s.statBtn} ${params.scope === 'customer' ? s.statOn : ''}`}
          onClick={() => guncelle({ scope: params.scope === 'customer' ? '' : 'customer' })}>
          <span className={`${s.statIcon} ${s.tViolet}`}><Store size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatNumber(o.customer_specific)}</span>
            <span className={s.statLabel}>Müşteriye özel · {formatNumber(o.customers)} cari</span>
          </span>
        </button>
      </div>

      <div className={s.toolbar}>
        <div className={s.searchBox}>
          <Search size={16} className={s.searchIcon} />
          <input type="search" className={s.search}
            placeholder="Kampanya adı, müşteri, ürün kodu veya grup…"
            value={arama} onChange={(e) => setArama(e.target.value)} aria-label="Kampanya ara" />
          {arama && (
            <button type="button" className={s.clearBtn} onClick={() => setArama('')} aria-label="Temizle">
              <X size={15} />
            </button>
          )}
        </div>
        <button type="button" className={`${s.ghostBtn} ${filtreAcik ? s.ghostOn : ''}`}
          onClick={() => setFiltreAcik((v) => !v)} aria-expanded={filtreAcik}>
          <SlidersHorizontal size={15} />
          <span className={s.btnText}>Filtreler</span>
          {acikFiltre > 0 && <span className={s.badge}>{acikFiltre}</span>}
        </button>
      </div>

      {filtreAcik && (
        <div className={s.filters}>
          <label className={s.field}>
            <span className={s.fieldLabel}>Tip</span>
            <select className={s.select} value={params.type}
              onChange={(e) => guncelle({ type: e.target.value })}>
              {TIPLER.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Kapsam</span>
            <select className={s.select} value={params.scope}
              onChange={(e) => guncelle({ scope: e.target.value })}>
              {KAPSAMLAR.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Ürün Grubu</span>
            <select className={s.select} value={params.group}
              onChange={(e) => guncelle({ group: e.target.value })}>
              <option value="">Tüm gruplar</option>
              {gruplar.map((g) => (
                <option key={g.code} value={g.code}>{g.label} ({formatNumber(g.count)})</option>
              ))}
            </select>
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Dönem</span>
            <select className={s.select} value={params.period}
              onChange={(e) => guncelle({ period: e.target.value })}>
              {DONEMLER.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Durum</span>
            <select className={s.select} value={params.status}
              onChange={(e) => guncelle({ status: e.target.value })}>
              {DURUMLAR.map((x) => <option key={x || 'all'} value={x}>{x || 'Tüm durumlar'}</option>)}
            </select>
          </label>
          {(acikFiltre > 0 || params.q) && (
            <button type="button" className={s.resetBtn} onClick={() => setSp({}, { replace: true })}>
              <X size={14} /> Temizle
            </button>
          )}
        </div>
      )}

      {params.code && (
        <div className={s.chipRow}>
          <span className={s.filterChip}>
            <Store size={13} /> Cari: <b>{params.code}</b>
            <button type="button" onClick={() => guncelle({ code: '' })} aria-label="Kaldır"><X size={13} /></button>
          </span>
        </div>
      )}

      {error && (
        <div className={s.alert}>
          <AlertTriangle size={18} /> <span>{error}</span>
          <button type="button" className={s.alertBtn} onClick={yenile}>Tekrar dene</button>
        </div>
      )}

      <section className={s.panel}>
        {loading && !ilk && <span className={s.loadBar} aria-hidden="true" />}
        {ilk ? (
          <div className={s.panelBody}><SkelPanel rows={7} head={false} /></div>
        ) : ogeler.length === 0 ? (
          <div className={s.empty}>
            <Inbox size={24} />
            <p className={s.emptyTitle}>Kampanya bulunamadı</p>
            <p className={s.emptyText}>Arama veya filtreleri değiştirmeyi deneyin.</p>
          </div>
        ) : (
          <>
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th className={`${s.th} ${s.thNo} ${s.thSort}`} onClick={() => sirala('id')}>
                      <span className={s.thInner}>#<SiraIkon alan="id" /></span>
                    </th>
                    <th className={`${s.th} ${s.thSort}`} onClick={() => sirala('name')}>
                      <span className={s.thInner}>Kampanya<SiraIkon alan="name" /></span>
                    </th>
                    <th className={s.th}>Kapsam</th>
                    <th className={`${s.th} ${s.thSort}`} onClick={() => sirala('customer')}>
                      <span className={s.thInner}>Müşteri<SiraIkon alan="customer" /></span>
                    </th>
                    <th className={`${s.th} ${s.thSort}`} onClick={() => sirala('rate')}>
                      <span className={s.thInner}>İndirim<SiraIkon alan="rate" /></span>
                    </th>
                    <th className={`${s.th} ${s.thSort}`} onClick={() => sirala('end')}>
                      <span className={s.thInner}>Dönem<SiraIkon alan="end" /></span>
                    </th>
                    <th className={s.th}>Durum</th>
                    <th className={`${s.th} ${s.thAct}`} />
                  </tr>
                </thead>
                <tbody>
                  {ogeler.map((k) => (
                    <React.Fragment key={k.id}>
                      <tr className={k.live ? '' : s.rowOff}>
                        <td className={`${s.td} ${s.tdNo}`}>{k.id}</td>
                        <td className={s.td}>
                          <span className={s.cell}>
                            <span className={s.name} title={k.name}>{k.name}</span>
                            {k.description && <span className={s.desc}>{k.description}</span>}
                          </span>
                        </td>
                        <td className={s.td}>
                          <span className={s.cell}>
                            <span className={s.typeTag}>
                              {k.type === 'Ürün' ? <Package size={12} /> : <Boxes size={12} />}
                              {k.type === 'Ürün' ? 'Ürün' : 'Grup'}
                            </span>
                            <span className={s.target} title={k.product_name || ''}>
                              {k.type === 'Ürün' ? (k.product || '-') : (k.group || '-')}
                            </span>
                          </span>
                        </td>
                        <td className={s.td}>
                          {k.code ? (
                            <span className={s.cell}>
                              <Link to={`/v2/admin/customers/${encodeURIComponent(k.code)}`}
                                className={s.custLink} title={k.customer_name || ''}>
                                {k.customer_name || k.code}
                              </Link>
                              <button type="button" className={s.codeBtn}
                                onClick={() => guncelle({ code: k.code })}>{k.code}</button>
                            </span>
                          ) : (
                            <span className={s.generalTag}><Globe size={12} /> Genel</span>
                          )}
                        </td>
                        <td className={s.td}><Oranlar rates={k.rates} /></td>
                        <td className={s.td}>
                          <span className={s.cell}>
                            <span className={s.dateMain}>{tarihTR(k.start)}</span>
                            <span className={`${s.dateSub} ${k.expired ? s.expired : ''}`}>
                              {suresiz(k.end) ? 'süresiz' : tarihTR(k.end)}
                            </span>
                          </span>
                        </td>
                        <td className={s.td}>
                          <span className={s.pills}>
                            <span className={`${s.pill} ${k.status === 'Aktif' ? s.pillGreen
                              : k.status === 'Pasif' ? s.pillGray : s.pillBlue}`}>{k.status}</span>
                            {k.live && <span className={`${s.pill} ${s.pillLive}`}>Yürürlükte</span>}
                            {k.expired && <span className={`${s.pill} ${s.pillAmber}`}>Süresi geçmiş</span>}
                          </span>
                        </td>
                        <td className={`${s.td} ${s.tdAct}`}>
                          <span className={s.actions}>
                            <button type="button" className={s.actBtn} title="Düzenle"
                              onClick={() => setModal(k)}><Pencil size={14} /></button>
                            <button type="button" className={s.actBtn} disabled={islemde}
                              title={k.status === 'Aktif' ? 'Pasife al' : 'Aktifleştir'}
                              onClick={() => durumDegistir(k)}>
                              {k.status === 'Aktif' ? <PowerOff size={14} /> : <Power size={14} />}
                            </button>
                            <button type="button" className={`${s.actBtn} ${s.actDanger}`}
                              title="Sil" onClick={() => setSilinecek(k)}><Trash2 size={14} /></button>
                          </span>
                        </td>
                      </tr>
                      {silinecek && silinecek.id === k.id && (
                        <tr className={s.confirmRow}>
                          <td className={s.confirmTd} colSpan={8}>
                            <div className={s.confirm}>
                              <AlertTriangle size={16} />
                              <span className={s.confirmText}>
                                <b>{k.name}</b> kalıcı olarak silinecek. Bu kampanyayı kullanan
                                müşterilerin fiyatları değişir.
                              </span>
                              <button type="button" className={s.confirmNo}
                                onClick={() => setSilinecek(null)} disabled={islemde}>Vazgeç</button>
                              <button type="button" className={s.confirmYes} onClick={sil} disabled={islemde}>
                                {islemde ? <Loader2 size={14} className={s.spin} /> : <Trash2 size={14} />} Evet, sil
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className={s.mList}>
              {ogeler.map((k) => (
                <li key={`m-${k.id}`} className={`${s.mItem} ${k.live ? '' : s.rowOff}`}>
                  <div className={s.mTop}>
                    <span className={s.cell}>
                      <span className={s.name}>{k.name}</span>
                      <span className={s.mSub}>
                        #{k.id} · {k.type === 'Ürün' ? (k.product || '-') : (k.group || '-')}
                      </span>
                    </span>
                    <Oranlar rates={k.rates} />
                  </div>
                  <div className={s.mMeta}>
                    {k.code ? (
                      <Link to={`/v2/admin/customers/${encodeURIComponent(k.code)}`} className={s.mLink}>
                        <Store size={12} /> {k.customer_name || k.code}
                      </Link>
                    ) : <span className={s.generalTag}><Globe size={12} /> Genel</span>}
                    <span>{tarihTR(k.start)} – {suresiz(k.end) ? 'süresiz' : tarihTR(k.end)}</span>
                  </div>
                  <div className={s.mFoot}>
                    <span className={s.pills}>
                      <span className={`${s.pill} ${k.status === 'Aktif' ? s.pillGreen
                        : k.status === 'Pasif' ? s.pillGray : s.pillBlue}`}>{k.status}</span>
                      {k.expired && <span className={`${s.pill} ${s.pillAmber}`}>Süresi geçmiş</span>}
                    </span>
                    <span className={s.actions}>
                      <button type="button" className={s.actBtn} onClick={() => setModal(k)}>
                        <Pencil size={14} />
                      </button>
                      <button type="button" className={s.actBtn} disabled={islemde}
                        onClick={() => durumDegistir(k)}>
                        {k.status === 'Aktif' ? <PowerOff size={14} /> : <Power size={14} />}
                      </button>
                      <button type="button" className={`${s.actBtn} ${s.actDanger}`}
                        onClick={() => setSilinecek(k)}><Trash2 size={14} /></button>
                    </span>
                  </div>
                  {silinecek && silinecek.id === k.id && (
                    <div className={s.confirm}>
                      <AlertTriangle size={16} />
                      <span className={s.confirmText}>Kalıcı olarak silinecek.</span>
                      <button type="button" className={s.confirmNo}
                        onClick={() => setSilinecek(null)} disabled={islemde}>Vazgeç</button>
                      <button type="button" className={s.confirmYes} onClick={sil} disabled={islemde}>
                        {islemde ? <Loader2 size={14} className={s.spin} /> : <Trash2 size={14} />} Sil
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>

            <div className={s.pager}>
              <span className={s.pagerInfo}>
                {formatNumber(ilkSira + 1)}–{formatNumber(ilkSira + ogeler.length)} / {formatNumber(toplam)}
              </span>
              <label className={s.perBox}>
                <span className={s.fieldLabel}>Sayfa başına</span>
                <select className={s.selectSm} value={params.per}
                  onChange={(e) => guncelle({ per: e.target.value })}>
                  {((data && data.page_sizes) || [25, 50, 100]).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              <div className={s.pagerBtns}>
                <button type="button" className={s.pageBtn} disabled={sayfa <= 1 || loading}
                  onClick={() => guncelle({ page: String(sayfa - 1) }, false)} aria-label="Önceki">
                  <ChevronLeft size={16} />
                </button>
                <span className={s.pageNo}>{formatNumber(sayfa)} / {formatNumber(sonSayfa)}</span>
                <button type="button" className={s.pageBtn} disabled={sayfa >= sonSayfa || loading}
                  onClick={() => guncelle({ page: String(sayfa + 1) }, false)} aria-label="Sonraki">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      <p className={s.note}>
        <TrendingDown size={14} />
        Bir kampanyada birden fazla oran varsa sırayla uygulanır: %20 + %5 toplamda %24 indirim eder.
      </p>

      <CampaignModal acik={Boolean(modal)} kampanya={modal} gruplar={gruplar}
        onKapat={() => setModal(null)}
        onKaydedildi={(mesaj) => { setModal(null); push(mesaj, 'success'); yenile(); }} />

      <BulkCampaignModal acik={topluAcik} gruplar={gruplar}
        onKapat={() => setTopluAcik(false)}
        onYuklendi={(mesaj) => { setTopluAcik(false); push(mesaj, 'success'); yenile(); }} />
      <ToastStack items={toastlar} onDismiss={dismiss} />
    </div>
  );
}
